/**
 * ListingCard — spec-first marketplace card.
 *
 * Redesigned per the Phase 1 card brief. Hierarchy top-to-bottom:
 *   [4:3 image + heart + carousel dots]
 *   ─ content (16px padding) ─
 *   [status pills row]  [tread/ET badge]
 *   [spec headline — e.g. "225/50R17 · 4 st"]
 *   [fitment + condition — e.g. "Volvo XC60 · Begagnat"]
 *   ─ hairline divider ─
 *   [price — brand-blue, 20/18px, weight 700]
 *   ─ hairline divider ─
 *   [seller row: avatar dot · type · ✓ · city · relative date]
 *
 * User-generated title is removed from the visible card — structured
 * fitment + condition replaces it. Seller row shows seller TYPE
 * (Privatperson / Återförsäljare), not the seller's display name;
 * this normalizes across inconsistent "Wheelplace" / "MM" / "CONSULTANT"
 * data. Relative dates are humanized Swedish.
 *
 * Data-model dependencies flagged for backend (Phase 4):
 *   - seller userType enum (business | private) — used; works today
 *   - author.metadata.isVerified bool — used; works today
 *   - publicData.brand (car make) — used when present; gracefully omitted
 *   - publicData.quantity — used when present
 *   - publicData.threaddepth — used when present
 *   - publicData.sasong / season — used when present
 *   - publicData.condition — used when present
 *   - price — used; fallback "Pris på förfrågan" when missing
 */
import React, { useState, useEffect } from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { isRenderedImage } from '../../util/renderedImageCore';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { customFormatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { listingPageLinkProps } from '../../util/listingSeoUrl';
import { cleanListingTitle, displayValue, plausibleTreadMm } from '../../util/listingTitle';

import { AspectRatioWrapper, NamedLink, ResponsiveImage, AvatarLarge } from '../../components';

import { useDispatch, useSelector } from 'react-redux';
import { updateFavourites } from '../../containers/SearchPage/SearchPage.duck';
import { updateTotalLikes } from '../../util/api';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  listingPricePieces,
  mountedTotalAmount,
  monteringServiceFor,
  monteringSetAmount,
  monteringIncludedFor,
  stockQuantityOf,
  distanceKm,
  formatDistance,
  freshnessMsOf,
} from '../../containers/SearchPage/resultCardData';
import { FIT_LEVEL, plateInfoModelName, wheelFitLevel } from '../../util/wheelFitment';
import { brandDisplayName } from '../../util/brandLabel';
import { listingTownName } from '../../util/listingTown';

import css from './ListingCard.module.css';

const { Money } = sdkTypes;

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const RocketLaunchIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M13.13 22.19l-1.63-3.83c1.57-.58 3.04-1.36 4.4-2.27l-2.77 6.1zM5.64 12.5l-3.83-1.63 6.1-2.77C7 9.46 6.22 10.93 5.64 12.5zM19.22 4c.28 0 .53.02.74.06.04.21.06.46.06.74 0 2.72-1.17 7.19-4.89 10.91-1.77 1.77-3.74 3.14-5.76 4.09l-3.04-3.04c.96-2.03 2.34-4 4.09-5.75C14.04 5.32 18.5 4 19.22 4zm0-2c-.67 0-5.7.1-10.25 4.64-2.18 2.18-3.65 4.43-4.61 6.47L2 14l4.91 2.13L8.77 18l2.13 4.91.88-2.37c2.03-.96 4.28-2.43 6.46-4.61 4.54-4.54 4.64-9.58 4.64-10.25 0-.66-.1-2.68-2.88-2.68zm-6.24 8.96c-.78.78-2.05.78-2.83 0-.78-.78-.78-2.05 0-2.83s2.05-.78 2.83 0c.78.78.78 2.05 0 2.83z" />
  </svg>
);

/* Lucide stroke-1.5 heart, inline so the card has no external icon
 * dependency. Separate filled variant for the liked state (swaps
 * fill + stroke rather than morphing the path). */
const HeartIcon = ({ filled }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth={filled ? 1.6 : 1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
    />
  </svg>
);

const ChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 5l7 7-7 7" />
  </svg>
);

/* Humanized Swedish relative-date formatter. Matches the notification
 * flyout / seller card / anywhere else we render "N min sedan". */
const formatRelativeSwedish = date => {
  if (!date) return null;
  const then = new Date(date).getTime();
  if (Number.isNaN(then)) return null;
  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diffSec < 60 * 60) return 'Just nu';
  const diffHour = Math.round(diffSec / 3600);
  if (diffHour < 24) return `${diffHour} tim sedan`;
  const diffDay = Math.round(diffHour / 24);
  if (diffDay === 1) return '1 dag sedan';
  if (diffDay < 7) return `${diffDay} dagar sedan`;
  const diffWeek = Math.round(diffDay / 7);
  if (diffWeek < 5) return `${diffWeek} ${diffWeek === 1 ? 'vecka' : 'veckor'} sedan`;
  const diffMonth = Math.round(diffDay / 30);
  if (diffMonth < 12) return `${diffMonth} ${diffMonth === 1 ? 'månad' : 'månader'} sedan`;
  return 'Över ett år sedan';
};

/* Absolute-date for the relative-date title attribute. */
const formatAbsoluteSwedish = date => {
  if (!date) return null;
  try {
    return new Date(date).toLocaleDateString('sv-SE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return null;
  }
};

/* Resolve the display value for a publicData enum field via the
 * listingField config. Falls back to the raw value if the config
 * doesn't list it (e.g. newly-added seller fields not yet in enum
 * definitions). */
const resolveFieldValue = (publicData, listingFields, field) => {
  const raw = publicData?.[field];
  if (raw == null || raw === '') return null;
  const fieldConfig = listingFields?.find(({ key }) => key === field);
  const isEnum = fieldConfig?.schemaType === 'enum' || fieldConfig?.schemaType === 'multi-enum';
  if (isEnum) {
    const option = fieldConfig.enumOptions?.find(o => o.option === raw);
    /* An option the config does not know prints as a word or not at all —
     * never as its key ("winterFriction"), P25009-486 BR-6. */
    return displayValue(raw, option?.label || null);
  }
  /* Not a configured enum (sasong / season are free keys on old listings):
   * the same rule. */
  return typeof raw === 'string' ? displayValue(raw) : raw;
};

/* Assemble the spec headline: tire size and quantity. */
const buildSpec = (publicData, listingFields) => {
  const size = (() => {
    const width = resolveFieldValue(publicData, listingFields, 'tireWidthFront');
    const profile = resolveFieldValue(publicData, listingFields, 'profileFront');
    const diameter = resolveFieldValue(publicData, listingFields, 'diameter');
    if (width && profile && diameter) return `${width}/${profile}R${diameter}`;
    if (diameter) return `${diameter}"`;
    return null;
  })();
  const quantity = publicData?.quantity;
  if (size && quantity) return `${size} · ${quantity} st`;
  if (size) return size;
  if (quantity) return `${quantity} st`;
  return null;
};

/* Assemble the fitment + condition line. The brand as a name, never its enum
 * key ("gtRadial", "other") — P25009-482 BR-11. */
const buildFitment = (publicData, listingFields) => {
  const brand = brandDisplayName(publicData?.brand, listingFields);
  const condition = resolveFieldValue(publicData, listingFields, 'condition');
  const parts = [];
  if (brand) parts.push(brand);
  if (condition) parts.push(condition);
  return parts.length > 0 ? parts.join(' · ') : null;
};

/* Seller type from userType; falls back to Privatperson if unknown. */
const sellerTypeLabel = userType =>
  userType === 'business' ? 'Återförsäljare' : 'Privatperson';

const PriceDisplay = ({ price, publicData, config, intl }) => {
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) return null;

  if (!price) {
    return <div className={css.priceMissing}>Pris på förfrågan</div>;
  }
  if (price.currency !== config.currency) {
    return (
      <div className={css.priceMissing}>
        <FormattedMessage id="ListingCard.unsupportedPrice" values={{ currency: price.currency }} />
      </div>
    );
  }
  const formatted = customFormatMoney(intl, price);
  return (
    <div className={css.price} title={formatted}>
      {formatted}
    </div>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showFavoriteIcons,
    isCustom,
    layout = 'grid',
    /* P25009-445: origin of the active search ({lat,lng}) for the distance
     * figure, and this listing's "same tyre from N sellers" group. Both are
     * search-page concerns and default to off elsewhere. */
    searchOrigin = null,
    sameTyreGroup = null,
  } = props;
  const isList = layout === 'list';
  const history = useHistory();
  const dispatch = useDispatch();
  const routeConfiguration = useRouteConfiguration();
  const classes = classNames(rootClassName || css.root, className);

  const currentListing = ensureListing(listing);
  const { title: rawTitle = '', price, publicData, metadata, createdAt } =
    currentListing.attributes;
  /* 18"" → 18" (P25009-486 BR-4). */
  const title = cleanListingTitle(rawTitle);
  // Route + params of the listing's breadcrumb URL, with a fallback to the
  // legacy path for listings that have no car make/model.
  const listingLink = listingPageLinkProps(currentListing);
  const author = ensureUser(listing.author);
  const authorMetadata = author?.attributes?.profile?.metadata || {};
  const authorProfile = author?.attributes?.profile;
  const userType = authorProfile?.publicData?.userType;
  const isBusinessUser = userType === 'business';
  const bio = authorProfile?.bio;

  const isBoosted = !!metadata?.isBoosted;

  const images = currentListing.images?.slice(0, 3) || [];
  const buildSlides = () => {
    if (isBusinessUser) {
      const avatarBioSlide = { type: 'avatarBio', author, bio };
      if (images.length === 1) return [images[0], avatarBioSlide];
      return [images[0], avatarBioSlide, ...images.slice(1)];
    }
    return images;
  };
  const slides = buildSlides();

  const { SearchPage: searchPageReducer, user: userReducer } = useSelector(state => state);
  const { currentUser } = userReducer;
  const { updateFavouritesInProgress } = searchPageReducer || {};
  const { isVerified } = authorMetadata;

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [liked, setLiked] = useState(false);
  const isOwnListing = author?.id?.uuid === currentUser?.id?.uuid;
  /* P25009-485 BR-2/BR-4 — the seller's own hidden trade-in, as it appears in
   * "Visa mitt lager". Never shown to anyone else: only the owner's own-stock
   * search returns hidden listings at all. */
  const isOwnHiddenTradein =
    isOwnListing && publicData?.isTradein === true && publicData?.isHide === true;
  const showHeartIcon = showFavoriteIcons && !isOwnListing;
  const userFavouriteListings =
    (currentUser && currentUser.attributes.profile.publicData.favoriteListings) || [];

  const isLiked = () => userFavouriteListings?.includes(currentListing.id.uuid);

  useEffect(() => {
    if (isLiked()) setLiked(true);
  }, [userFavouriteListings]);

  const toggleFavourite = event => {
    event.preventDefault();
    event.stopPropagation();
    if (!currentUser) {
      const state = { from: `${location.pathname}${location.search}${location.hash}` };
      history.push(createResourceLocatorString('SignupPage', routeConfiguration, {}, {}), state);
      return;
    }
    if (updateFavouritesInProgress) return;

    setLiked(!liked);
    const listingId = currentListing.id.uuid;
    const action = isLiked() ? 'unlike' : 'like';
    let newListings = userFavouriteListings ? [...userFavouriteListings] : [];
    if (isLiked()) {
      newListings = newListings.filter(l => l !== listingId);
    } else {
      newListings.push(listingId);
    }
    dispatch(updateFavourites(newListings))
      .then(() => updateTotalLikes({ listingId, action }))
      .catch(e => console.error('Failed updating favourites or like count', e));
  };

  const nextSlide = e => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentSlideIndex(prev => (prev + 1) % slides.length);
  };

  const prevSlide = e => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentSlideIndex(prev => (prev - 1 + slides.length) % slides.length);
  };

  const currentSlide = slides[currentSlideIndex];

  const { aspectWidth = 4, aspectHeight = 3, variantPrefix = 'listing-card' } =
    config.layout.listingImage;
  const allVariants =
    currentSlide && currentSlide.type !== 'avatarBio' && currentSlide.attributes?.variants
      ? Object.keys(currentSlide.attributes.variants)
      : [];
  let variants = allVariants.filter(
    k => k.startsWith(variantPrefix) || k.startsWith('landscape-crop') || k.startsWith('scaled-')
  );
  if (allVariants.length > 0 && variants.length === 0) variants = allVariants;

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  // The buyer's car, for the fit badge below (read before any early return).
  const buyerCar = useSelector(state => state.currentLicensePlate?.currentLicensePlate || null);
  const isAdverts = publicData?.listingType === 'admin-adverts';
  const link = publicData?.externalLink;
  const subdescription = publicData?.subDescription;

  /* ---- Advert card branch. Admin-authored external ads use a
   * simpler layout; mirror the main card's layout switch so ads
   * don't render a full-page vertical card in the middle of a
   * list-mode row stream. */
  if (isAdverts) {
    const advertClasses = classNames(classes, { [css.rootList]: isList });
    return (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className={advertClasses}
      >
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
          {...setActivePropsMaybe}
        >
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title}
            image={currentSlide}
            variants={variants}
            sizes={renderSizes}
          />
        </AspectRatioWrapper>
        <div className={css.info}>
          <div className={css.advertTitle}>{title}</div>
          {subdescription ? <div className={css.advertSub}>{subdescription}</div> : null}
        </div>
      </a>
    );
  }

  /* ---- Main listing card ---- */
  const listingFields = config?.listing?.listingFields;
  const spec = buildSpec(publicData, listingFields);
  const fitment = buildFitment(publicData, listingFields);

  /* P25009-482 BR-4 — with the buyer's car known (typed once, carried by
   * util/savedCar.js), every card says whether it fits: computed here, from
   * the listing's own facts, with the same check the buy box runs. Only a
   * clear answer is shown; an unsure one shows nothing. A close tyre size —
   * what a car search falls back to when nothing has the car's own size
   * (P25009-492) — says so rather than "Passar inte". */
  const fitLevel = buyerCar
    ? wheelFitLevel({ listingPublicData: publicData || {}, licensePlateInfo: buyerCar })
    : null;
  const buyerModel = buyerCar ? plateInfoModelName(buyerCar) : '';
  const fitBadge =
    fitLevel === FIT_LEVEL.EXACT ? (
      <div className={classNames(css.fitBadge, css.fitBadgeFits)}>
        ✓ Passar din {buyerModel || 'bil'}
      </div>
    ) : fitLevel === FIT_LEVEL.CLOSE ? (
      <div className={classNames(css.fitBadge, css.fitBadgeClose)}>≈ Närliggande storlek</div>
    ) : fitLevel === FIT_LEVEL.NO ? (
      <div className={classNames(css.fitBadge, css.fitBadgeNoFit)}>Passar inte</div>
    ) : null;
  const season = resolveFieldValue(publicData, listingFields, 'sasong')
    || resolveFieldValue(publicData, listingFields, 'season');
  const treadDepth = resolveFieldValue(publicData, listingFields, 'threaddepth');
  const etOffset = resolveFieldValue(publicData, listingFields, 'etFront');
  /* The town, never a postcode, a street or a county key (P25009-425 BR-3). */
  const city = listingTownName(publicData, listingFields);

  /* ---- P25009-445 search-card data ---- */
  const formatSubunits = (amount, currency) =>
    Number.isFinite(amount) && currency
      ? customFormatMoney(intl, new Money(amount, currency))
      : null;

  /* Tyre type ("Nordisk friktion", "Dubbdäck", …) via the hosted field config. */
  const tiretypeLabel = resolveFieldValue(publicData, listingFields, 'tiretype');

  /* EU tyre label — written by the supplier feed when known; absent on most
   * listings today, and the row simply doesn't render then (BR-6). */
  const euLabel = publicData?.euLabel || null;
  const euFuel = euLabel?.fuel ? String(euLabel.fuel).toUpperCase() : null;
  const euWet = euLabel?.wetGrip ? String(euLabel.wetGrip).toUpperCase() : null;
  const euNoiseDb = Number.isFinite(Number(euLabel?.noiseDb)) ? Number(euLabel.noiseDb) : null;
  const hasEuLabel = !!(euFuel || euWet || euNoiseDb);

  /* Sharetribe stock — null when the listing doesn't track stock (private
   * sellers), and no stock line is shown then. */
  const stockQuantity = stockQuantityOf(currentListing);
  const stockLine =
    stockQuantity === null
      ? null
      : stockQuantity <= 0
        ? { text: 'Slutsåld', level: 'out' }
        : stockQuantity <= 2
          ? { text: `Få kvar (${stockQuantity})`, level: 'low' }
          : { text: `${stockQuantity} i lager`, level: 'in' };

  /* Seller montering service — only where it applies to this listing, priced
   * for the set the card shows (P25009-484). */
  const monteringService = monteringServiceFor(author, publicData);
  const monteringAmount = monteringSetAmount(currentListing);
  const monteringIncluded = monteringIncludedFor(currentListing);

  /* Per-piece / per-set price split. */
  const pricePieces = listingPricePieces(currentListing);
  const mountedTotal = mountedTotalAmount(currentListing);

  /* P25009-486 BR-3 — the card's main price is the price for the set
   * ("17 436 kr" / "för 4 hjul · 4 359 kr/st"), then what it costs fitted
   * ("Klart monterat 17 835 kr"). Same block in grid and list view. */
  const setPriceBlock =
    pricePieces && pricePieces.setSize > 1 ? (
      <div className={css.priceBlock}>
        <div className={css.price}>{formatSubunits(pricePieces.set, pricePieces.currency)}</div>
        <div className={css.priceSetLine}>
          för {pricePieces.setSize} {pricePieces.noun.many} ·{' '}
          {formatSubunits(pricePieces.piece, pricePieces.currency)}/st
        </div>
        {mountedTotal !== null ? (
          <div className={css.priceMountedLine}>
            Klart monterat {formatSubunits(mountedTotal, pricePieces.currency)}
          </div>
        ) : null}
      </div>
    ) : null;

  /* Where and how the buyer gets it: "Hämta i Hisings Kärra · eller frakt".
   * Pickup is always offered (P25009-484); freight only when the seller
   * ships. No date — no listing carries a lead time to promise one from. */
  const pickupCity = city;
  const whereLine = pickupCity
    ? `Hämta i ${pickupCity}${publicData?.shippingEnabled ? ' · eller frakt' : ''}`
    : publicData?.shippingEnabled
      ? 'Frakt'
      : null;

  /* "Göteborg · ca 12 km" whenever the buyer's area is known — the search's
   * origin, a "Använd min plats" or the profile's ort (P25009-425 BR-3). The
   * area is rounded to about a kilometre, so the figure says "ca" (BR-6). */
  const geolocation = currentListing.attributes.geolocation;
  const distance =
    searchOrigin && geolocation ? formatDistance(distanceKm(searchOrigin, geolocation)) : null;
  const distanceText = distance ? `ca ${distance}` : null;
  const placeText = [city, distanceText].filter(Boolean).join(' · ') || null;

  /* Local options (P25009-425 BR-4): collecting from a store — a business
   * seller with a known town; pickup is always offered (P25009-484) — and the
   * seller's own montering, the same service the list view prices. */
  const storePickup = isBusinessUser && !!pickupCity;
  const monteringLabel = monteringIncluded ? 'Montering ingår' : 'Montering hos säljaren';

  const groupLine =
    sameTyreGroup && sameTyreGroup.sellerCount > 1
      ? `Samma ${sameTyreGroup.noun?.one || 'produkt'} från ${sameTyreGroup.sellerCount} säljare` +
        (Number.isFinite(sameTyreGroup.fromPiece)
          ? ` — från ${formatSubunits(sameTyreGroup.fromPiece, sameTyreGroup.currency)}/st`
          : '')
      : null;

  const isRimOnly =
    publicData?.listingType === 'rim' || publicData?.listingType === 'businesswheel';

  /* Tread depth shown for tire-bearing listings; ET offset shown for
   * rim-only. Only a believable depth (0–20 mm): "7887mm" is a typo, and
   * an unparsable value is not shown at all (P25009-486 BR-6). */
  const treadMm = plausibleTreadMm(treadDepth);

  const rightBadgeLabel = isRimOnly
    ? etOffset
      ? `ET${etOffset}`
      : null
    : treadMm != null
      ? `${treadMm}mm`
      : null;

  /* Tier coloring for tread depth per the spec:
   *  ≥5mm: neutral (plenty of life left)
   *  3–4.9mm: amber (worn)
   *  <3mm: red (near legal minimum 1.6mm — safety signal) */
  const treadTier =
    !isRimOnly && treadMm != null
      ? treadMm >= 5
        ? 'neutral'
        : treadMm >= 3
          ? 'warning'
          : 'critical'
      : 'neutral';

  /* Time-based flags — fresh (<48h → `Ny` pill) and stale (>6mo →
   * `Äldre` pill + muted opacity). */
  const NOW = Date.now();
  const createdAtMs = createdAt ? new Date(createdAt).getTime() : null;
  const isFresh = createdAtMs ? NOW - createdAtMs < 48 * 60 * 60 * 1000 : false;
  const STALE_THRESHOLD_MS = 6 * 30 * 24 * 60 * 60 * 1000;
  /* "Äldre" goes by the last price/stock update, not only the creation date —
   * a two-year-old listing repriced last week is not old (P25009-486). */
  const freshnessMs = freshnessMsOf(currentListing) ?? createdAtMs;
  const isStale = freshnessMs ? NOW - freshnessMs > STALE_THRESHOLD_MS : false;

  const displayName = authorProfile?.displayName || '';
  const sellerInitial = displayName ? displayName.trim().charAt(0).toUpperCase() : '?';
  const sellerLabel = sellerTypeLabel(userType);
  const relativeDate = formatRelativeSwedish(createdAt);
  const absoluteDate = formatAbsoluteSwedish(createdAt);

  /* Full aria-label — composed from spec + fitment + price + seller
   * for screen-reader users (spec §10). */
  const ariaLabel = [
    spec,
    fitment,
    treadDepth && !isRimOnly ? `mönsterdjup ${rightBadgeLabel}` : null,
    price ? customFormatMoney(intl, price) : 'pris på förfrågan',
    `från ${sellerLabel}${city ? ` i ${city}` : ''}`,
    distanceText ? `${distanceText} bort` : null,
    relativeDate ? `publicerat ${relativeDate.toLowerCase()}` : null,
  ]
    .filter(Boolean)
    .join(', ');

  /* Guardrail for visibly-broken cards: if a listing has no spec
   * AND no price AND no fitment, hide it entirely rather than
   * render an empty frame. The proper fix is publish-time validation
   * upstream (flagged for backend); this is defense in depth. */
  if (!spec && !fitment && !price) {
    return null;
  }

  const rootClasses = classNames(classes, {
    [css.stale]: isStale,
    [css.rootList]: isList,
  });

  /* ---- List-view JSX — dedicated layout that actually uses the
   * full row width. Structure:
   *   [160×120 image] [main: spec + fitment + seller] [price + tread] [♡]
   * This is a different DOM shape from the grid card so it's built
   * here as its own branch rather than CSS-munged into the grid
   * JSX. Shared primitives (PriceDisplay, sellerRow, etc.) are
   * reused verbatim so hierarchy rules stay consistent. */
  if (isList) {
    return (
      <NamedLink className={rootClasses} {...listingLink} aria-label={ariaLabel}>
        {isBoosted ? (
          <div className={css.boostBadge} aria-label="Framhävd">
            <RocketLaunchIcon className={css.boostIcon} />
          </div>
        ) : null}

        {showHeartIcon ? (
          <button
            type="button"
            className={classNames(css.favoriteButton, {
              [css.favoriteButtonActive]: liked,
              [css.favoriteButtonDisabled]: updateFavouritesInProgress,
            })}
            onClick={toggleFavourite}
            aria-pressed={liked}
            aria-label={liked ? 'Ta bort från favoriter' : 'Spara till favoriter'}
          >
            <HeartIcon filled={liked} />
          </button>
        ) : null}

        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
          {...setActivePropsMaybe}
        >
          {currentSlide && currentSlide.type === 'avatarBio' ? (
            <div className={css.avatarBioSlide}>
              <AvatarLarge user={author} className={css.businessAvatar} />
            </div>
          ) : (
            <LazyImage
              rootClassName={css.rootForImage}
              alt={title || spec || 'Annons'}
              image={currentSlide}
              variants={variants}
              sizes={renderSizes}
            />
          )}
          {isRenderedImage(publicData, currentSlide) ? (
            <span className={css.illustrationBadge}>
              <FormattedMessage id="ListingCard.illustrationBadge" />
            </span>
          ) : null}
        </AspectRatioWrapper>

        <div className={css.listBody}>
          {/* Hidden listing title — preserves the seller-written
            * copy for SEO (Google indexes visually-hidden text) and
            * for screen readers, without competing with the
            * spec-first visual hierarchy. */}
          {title ? <h3 className={css.visuallyHidden}>{title}</h3> : null}
          <div className={css.listMain}>
            {title ? <div className={css.listingTitle}>{title}</div> : null}
            {(isFresh || isStale || season || isOwnHiddenTradein) ? (
              <div className={css.listPills}>
                {isOwnHiddenTradein ? <span className={css.pillTradein}>Inbyte · dold</span> : null}
                {isFresh ? <span className={css.pillPrimary}>Ny</span> : null}
                {isStale && !isFresh ? <span className={css.pillStale}>Äldre</span> : null}
                {season ? <span className={css.pillNeutral}>{season}</span> : null}
              </div>
            ) : null}

            {spec ? (
              <div className={css.spec}>
                {spec}
                {tiretypeLabel ? <span className={css.specTiretype}> · {tiretypeLabel}</span> : null}
              </div>
            ) : null}
            {fitment ? <div className={css.fitment}>{fitment}</div> : null}
            {fitBadge}

            {/* EU tyre label — fuel class, wet grip, rolling noise (BR-6). */}
            {hasEuLabel ? (
              <div className={css.euLabelRow} aria-label="EU-däckmärkning">
                {euFuel ? <span className={css.euChip}><b>{euFuel}</b> bränsle</span> : null}
                {euWet ? <span className={css.euChip}><b>{euWet}</b> våt</span> : null}
                {euNoiseDb ? <span className={css.euChip}>{euNoiseDb} dB</span> : null}
              </div>
            ) : null}

            <div className={css.listSellerRow}>
              {isVerified ? (
                <span className={css.sellerVerified} title="Verifierad" aria-label="Verifierad">
                  <CheckIcon />
                </span>
              ) : null}
              <span className={css.sellerType}>
                {isBusinessUser && displayName ? displayName : sellerLabel}
              </span>
              {placeText ? (
                <>
                  <span className={css.sellerDot} aria-hidden="true">·</span>
                  <span className={css.sellerCity}>{placeText}</span>
                </>
              ) : null}
              {stockLine ? (
                <>
                  <span className={css.sellerDot} aria-hidden="true">·</span>
                  <span
                    className={classNames(css.stockLine, {
                      [css.stockLow]: stockLine.level === 'low',
                      [css.stockOut]: stockLine.level === 'out',
                    })}
                  >
                    {stockLine.text}
                  </span>
                </>
              ) : null}
              {!stockLine && relativeDate ? (
                <>
                  <span className={css.sellerDot} aria-hidden="true">·</span>
                  <span className={css.sellerDate} title={absoluteDate || undefined}>
                    {relativeDate}
                  </span>
                </>
              ) : null}
            </div>

            {/* Local options (P25009-425 BR-4). Mounting at the seller — a
              * fact about the seller's services, stated without inventing a
              * date (there is no booking calendar to promise one from). */}
            {storePickup || monteringService ? (
              <div className={css.localOptions}>
                {storePickup ? <span className={css.pickupLine}>Hämta i butik</span> : null}
                {monteringService ? (
                  <span className={css.mountingLine}>
                    {monteringLabel}
                    {monteringAmount !== null && pricePieces
                      ? ` · ${formatSubunits(monteringAmount, pricePieces.currency)}`
                      : ''}
                  </span>
                ) : null}
              </div>
            ) : null}

            {whereLine ? <div className={css.whereLine}>{whereLine}</div> : null}
            {groupLine ? <div className={css.sameTyreLine}>{groupLine}</div> : null}
          </div>

          <div className={css.listAside}>
            {rightBadgeLabel ? (
              <span
                className={classNames(css.specBadge, {
                  [css.specBadgeWarning]: treadTier === 'warning',
                  [css.specBadgeCritical]: treadTier === 'critical',
                })}
              >
                {rightBadgeLabel}
              </span>
            ) : null}
            {setPriceBlock || (
              <PriceDisplay price={price} publicData={publicData} config={config} intl={intl} />
            )}
            {pricePieces ? <span className={css.buyNow}>Köp nu</span> : null}
          </div>
        </div>
      </NamedLink>
    );
  }

  return (
    <NamedLink className={rootClasses} {...listingLink} aria-label={ariaLabel}>
      {/* Boosted badge — top-left (was shifting based on heart presence;
        * now fixed position). */}
      {isBoosted ? (
        <div className={css.boostBadge} aria-label="Framhävd">
          <RocketLaunchIcon className={css.boostIcon} />
        </div>
      ) : null}

      {/* Heart — absolute top-right, backdrop-blur for contrast on any
        * image. Click stops propagation so the card link doesn't fire. */}
      {showHeartIcon ? (
        <button
          type="button"
          className={classNames(css.favoriteButton, {
            [css.favoriteButtonActive]: liked,
            [css.favoriteButtonDisabled]: updateFavouritesInProgress,
          })}
          onClick={toggleFavourite}
          aria-pressed={liked}
          aria-label={liked ? 'Ta bort från favoriter' : 'Spara till favoriter'}
        >
          <HeartIcon filled={liked} />
        </button>
      ) : null}

      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        {currentSlide && currentSlide.type === 'avatarBio' ? (
          <div className={css.avatarBioSlide}>
            <AvatarLarge user={author} className={css.businessAvatar} />
            {bio ? <div className={css.businessBio}>{bio}</div> : null}
          </div>
        ) : (
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title || spec || 'Annons'}
            image={currentSlide}
            variants={variants}
            sizes={renderSizes}
          />
        )}
        {isRenderedImage(publicData, currentSlide) ? (
          <span className={css.illustrationBadge}>
            <FormattedMessage id="ListingCard.illustrationBadge" />
          </span>
        ) : null}

        {slides.length > 1 && !isCustom ? (
          <>
            {/* Arrows — only revealed on desktop hover (CSS handles the
              * visibility). Hidden on touch via pointer:fine media query. */}
            <div className={css.imageNav}>
              <button
                type="button"
                onClick={prevSlide}
                className={css.prevButton}
                aria-label="Föregående bild"
              >
                <ChevronLeft />
              </button>
              <button
                type="button"
                onClick={nextSlide}
                className={css.nextButton}
                aria-label="Nästa bild"
              >
                <ChevronRight />
              </button>
            </div>
            {/* Dot pagination — bottom-centered over the image. */}
            <div className={css.pagination} aria-hidden="true">
              {slides.map((_, i) => (
                <span
                  key={i}
                  className={classNames(css.dot, { [css.dotActive]: i === currentSlideIndex })}
                />
              ))}
            </div>
          </>
        ) : null}
      </AspectRatioWrapper>

      <div className={css.info}>
        {/* Hidden listing title — preserves the seller-written copy
          * for SEO (Google indexes visually-hidden text) without
          * competing with the spec-first visual hierarchy. */}
        {title ? <h3 className={css.visuallyHidden}>{title}</h3> : null}
        {title ? <div className={css.listingTitle}>{title}</div> : null}

        {/* Status pills + tread/ET badge row. Rendered only when at
          * least one of the three slots has content, so cards without
          * pills don't eat 30px of whitespace. */}
        {(isFresh || isStale || season || rightBadgeLabel) ? (
          <div className={css.pillRow}>
            <div className={css.pillsLeft}>
              {/* Priority order: Ny (freshness) > Äldre (staleness) >
                * Season > everything else. Fresh wins if both could
                * apply (shouldn't, given 48h vs 6mo thresholds). */}
              {isOwnHiddenTradein ? <span className={css.pillTradein}>Inbyte · dold</span> : null}
              {isFresh ? <span className={css.pillPrimary}>Ny</span> : null}
              {isStale && !isFresh ? <span className={css.pillStale}>Äldre</span> : null}
              {season ? <span className={css.pillNeutral}>{season}</span> : null}
            </div>
            {rightBadgeLabel ? (
              <span
                className={classNames(css.specBadge, {
                  [css.specBadgeWarning]: treadTier === 'warning',
                  [css.specBadgeCritical]: treadTier === 'critical',
                })}
              >
                {rightBadgeLabel}
              </span>
            ) : null}
          </div>
        ) : null}

        {/* Spec headline — the card's most important line. */}
        {spec ? <div className={css.spec}>{spec}</div> : null}

        {/* Fitment + condition — structured replacement for the
          * user-generated title. */}
        {fitment ? <div className={css.fitment}>{fitment}</div> : null}
        {fitBadge}

        <div className={css.divider} />

        {/* Price for the set first, then per piece, then fitted (BR-3). */}
        {setPriceBlock || (
          <PriceDisplay price={price} publicData={publicData} config={config} intl={intl} />
        )}

        {/* "● 4 i lager · Hämta i Hisings Kärra · eller frakt" (BR-3). */}
        {stockLine || whereLine ? (
          <div className={css.availabilityLine}>
            {stockLine ? (
              <span
                className={classNames(css.stockLine, {
                  [css.stockLow]: stockLine.level === 'low',
                  [css.stockOut]: stockLine.level === 'out',
                })}
              >
                {stockLine.text}
              </span>
            ) : null}
            {stockLine && whereLine ? <span aria-hidden="true"> · </span> : null}
            {whereLine ? <span>{whereLine}</span> : null}
          </div>
        ) : null}

        {/* Local options (P25009-425 BR-4) — "Hämta i butik", "Montering hos
          * säljaren" — where the seller offers them. */}
        {storePickup || monteringService ? (
          <div className={css.localOptions}>
            {storePickup ? <span className={css.pickupLine}>Hämta i butik</span> : null}
            {monteringService ? <span className={css.mountingLine}>{monteringLabel}</span> : null}
          </div>
        ) : null}

        <div className={css.divider} />

        {/* Seller row — type, not name; single verification check. */}
        <div className={css.sellerRow}>
          <span className={css.sellerAvatar} aria-hidden="true">
            {sellerInitial}
          </span>
          <span className={css.sellerType}>{sellerLabel}</span>
          {isVerified ? (
            <span className={css.sellerVerified} title="Verifierad" aria-label="Verifierad">
              <CheckIcon />
            </span>
          ) : null}
          <span className={css.sellerDot} aria-hidden="true">·</span>
          {placeText ? <span className={css.sellerCity}>{placeText}</span> : null}
          {/* With a distance the row is "Göteborg · ca 12 km": the date gives
            * way, or the narrow card cuts the distance off (P25009-425). */}
          {placeText && relativeDate && !distanceText ? (
            <span className={css.sellerDot} aria-hidden="true">·</span>
          ) : null}
          {relativeDate && !distanceText ? (
            <span
              className={css.sellerDate}
              title={absoluteDate || undefined}
            >
              {relativeDate}
            </span>
          ) : null}
        </div>
      </div>
    </NamedLink>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,
  renderSizes: string,
  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);
